import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import {
  ArrowRight,
  FlaskConical,
  Factory,
  Server,
  Hospital,
  Building2,
  Train,
  Truck,
  Clock,
  ShieldCheck,
  Banknote,
  type LucideIcon,
} from "lucide-react";
import { MiLogoMark } from "@/components/MiLogoMark";
import { Img, SIZES } from "@/components/Img";
import { type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { pageHead } from "@/i18n/head";
import { omosStrings } from "@/i18n/content/omos";

/** <head> for the about-us page: pageHead with breadcrumb (not a Service, no schema). */
export function omosHead(locale: Locale) {
  const s = omosStrings[locale];
  return pageHead("omos", locale, s.meta, {
    breadcrumbName: s.hero.title,
  });
}

// Icons aligned by index with about.branchLabels in the catalog.
const branchIcons: LucideIcon[] = [FlaskConical, Factory, Server, Hospital, Building2, Train, Truck];
// Icons aligned by index with tqe.cards in the catalog.
const tqeIcons: LucideIcon[] = [Clock, ShieldCheck, Banknote];

// Team members: names/images stay here; roles come from the catalog by index.
const teamMembers = [
  { name: "Sonny Christensen", img: "/wp-content/uploads/Sonny_portraet_400x527.jpg" },
  { name: "Jonas Terkild Christensen", img: "/wp-content/uploads/Jonas_portraet_400x527.jpg" },
  { name: "Simon Terkild Christensen", img: "/wp-content/uploads/Simon_portraet_400x527.jpg" },
  { name: "Lars Christiansen", img: "/assets-photos/lars-christiansen.png" },
];

export function OmOsPage({ locale }: { locale: Locale }) {
  const t = omosStrings[locale];
  const aboutP2 = t.about.p2.replace("{{break}}", "\n\n");

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight">{t.hero.title}</h1>
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.hero.p1}</p>
          <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.hero.p2}</p>
          <p className="mt-4 text-lg text-foreground/85">{t.hero.p3}</p>
        </div>
      </section>

      {/* Om MI Support + Brancher */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.about.heading}</h2>
          <p className="mt-6 leading-relaxed">{t.about.p1}</p>
          <p className="mt-4 leading-relaxed whitespace-pre-line">{aboutP2}</p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 text-left">
            {t.about.branchLabels.map((label, i) => {
              const Icon = branchIcons[i] ?? FlaskConical;
              return (
                <Link
                  key={label}
                  to={pathFor("brancher", locale)}
                  className="flex items-center gap-3 rounded-xl bg-white/10 p-4 hover:bg-white/20 transition-colors"
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="text-sm font-medium leading-tight">{label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Historien */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div className="grid grid-cols-3 gap-3 order-2 lg:order-1">
            <Img src="/wp-content/uploads/3.jpg" alt={t.history.photoAlts[0]} className="rounded-xl w-full aspect-[3/4] object-cover" sizes={SIZES.card3} />
            <Img src="/wp-content/uploads/1.jpg" alt={t.history.photoAlts[1]} className="rounded-xl w-full aspect-[3/4] object-cover" sizes={SIZES.card3} />
            <Img src="/wp-content/uploads/0.jpg" alt={t.history.photoAlts[2]} className="rounded-xl w-full aspect-[3/4] object-cover" sizes={SIZES.card3} />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.history.heading}</h2>
            <p className="mt-6 text-foreground/90 leading-relaxed">{t.history.p1}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.history.p2}</p>
          </div>
        </div>
      </section>

      {/* Samarbejdspartner */}
      <section className="bg-secondary py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.partner.heading}</h2>
          <p className="mt-6 text-foreground/95 leading-relaxed">{t.partner.p1}</p>
          <p className="mt-4 text-foreground/95 leading-relaxed">{t.partner.p2}</p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {t.partner.stats.map((s) => (
              <div key={s.label} className="rounded-2xl bg-background p-8">
                <div className="text-4xl md:text-5xl font-bold text-primary">{s.value}</div>
                <p className="mt-3 text-foreground/90 text-sm">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alle faser */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-4 md:px-8 text-center">
          <p className="text-lg text-foreground/90 leading-relaxed">{t.phases.body}</p>
          <Img
            src="/wp-content/uploads/PAM-projekt-og-milepaelsplan.png"
            alt={t.phases.imgAlt}
            className="mt-12 mx-auto w-full max-w-4xl h-auto"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Full-service + IT */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-8 md:grid-cols-2">
          <article className="rounded-2xl bg-secondary overflow-hidden">
            <Img
              src="/wp-content/uploads/MISupportIT_NTK3787_1350x900.jpg"
              alt={t.fullService.imgAlt}
              className="w-full aspect-[16/10] object-cover"
              sizes={SIZES.split}
            />
            <div className="p-8">
              <h3 className="text-2xl font-bold">{t.fullService.heading}</h3>
              <p className="mt-4 text-foreground/95 leading-relaxed">{t.fullService.p1}</p>
              <p className="mt-4 text-foreground/95 leading-relaxed">{t.fullService.p2}</p>
            </div>
          </article>
          <article className="rounded-2xl bg-secondary overflow-hidden">
            <Img
              src="/wp-content/uploads/MISupportIT_NTK4094_1350x900.jpg"
              alt={t.it.imgAlt}
              className="w-full aspect-[16/10] object-cover"
              sizes={SIZES.split}
            />
            <div className="p-8">
              <h3 className="text-2xl font-bold">{t.it.heading}</h3>
              <p className="mt-4 text-foreground/95 leading-relaxed">{t.it.p1}</p>
              <a
                href="https://www.misupportit.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 btn-cta"
              >
                {t.it.cta} <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </article>
        </div>
      </section>

      {/* Quote sprog */}
      <section className="py-16 bg-section-light text-section-light-foreground">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-2xl md:text-3xl font-medium italic leading-snug">
           {t.languageQuote.text}
          </p>
          <p className="mt-8 font-semibold">{t.languageQuote.name}</p>
          <p className="text-section-light-foreground/70 text-sm">{t.languageQuote.role}</p>
        </div>
      </section>

      {/* Worldwide */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.worldwide.heading}</h2>
          <p className="mt-6 text-foreground/90 leading-relaxed">{t.worldwide.p1}</p>
          <p className="mt-4 text-foreground/90 leading-relaxed">{t.worldwide.p2}</p>
          <p className="mt-4 text-foreground/90 leading-relaxed">{t.worldwide.p3}</p>
          <div className="mt-10 rounded-xl overflow-hidden">
            <Img
              src="/wp-content/uploads/World-map-MI-Support-locations.png"
              alt={t.worldwide.mapAlt}
              className="mx-auto w-full h-auto"
              sizes={SIZES.split}
            />
          </div>
          <Link
            to={pathFor("samarbejdspartnere", locale)}
            className="mt-10 btn-cta"
          >
            {t.worldwide.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Værdier + Tid kvalitet økonomi */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.values.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.values.intro}</p>
        </div>
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-start mt-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.tqe.heading}</h2>
            <p className="mt-6 text-foreground/90 leading-relaxed">{t.tqe.p1}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.tqe.p2}</p>
            <Img
              src="/wp-content/uploads/MISupportIT_NTK3754-scaled.jpg"
              alt={t.tqe.imgAlt}
              className="mt-8 rounded-2xl w-full aspect-[16/10] object-cover"
              sizes={SIZES.split}
            />
          </div>
          <div className="space-y-6">
            {t.tqe.cards.map((card, i) => {
              const Icon = tqeIcons[i] ?? Clock;
              return (
                <div key={card.title} className="rounded-2xl bg-secondary p-8">
                  <div className="flex items-center gap-3">
                    <Icon className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-bold">{card.title}</h3>
                  </div>
                  <p className="mt-3 text-foreground/95 leading-relaxed">{card.p1}</p>
                  <p className="mt-3 text-foreground/95 leading-relaxed">{card.p2}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bæredygtighed */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <Img
            src="/wp-content/uploads/Kontor-gruppe-4_1400x1400.jpg"
            alt={t.sustainability.imgAlt}
            className="rounded-2xl w-full aspect-square object-cover"
            sizes={SIZES.split}
          />
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.sustainability.heading}</h2>
            <p className="mt-6 leading-relaxed">{t.sustainability.p1}</p>
            <p className="mt-4 leading-relaxed">{t.sustainability.p2}</p>
            <p className="mt-4 leading-relaxed">{t.sustainability.p3}</p>
            <p className="mt-4 leading-relaxed">{t.sustainability.p4}</p>
          </div>
        </div>
      </section>

      {/* Diversitet */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.diversity.heading}</h2>
            <p className="mt-6 text-foreground/90 leading-relaxed">{t.diversity.p1}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.diversity.p2}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.diversity.p3}</p>
            <p className="mt-4 text-foreground/90 leading-relaxed">{t.diversity.p4}</p>
            <Link
              to={pathFor("karriere", locale)}
              className="mt-8 btn-cta"
            >
              {t.diversity.ctaBefore} <MiLogoMark className="h-4 w-auto mx-0.5" /> {t.diversity.ctaAfter} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Img
            src="/wp-content/uploads/Sonny-kontor1_1400x1400.jpg"
            alt={t.diversity.imgAlt}
            className="rounded-2xl w-full aspect-square object-cover"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Ledelsen */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center">{t.leadership.heading}</h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {teamMembers.map((p, i) => (
              <div key={p.name} className="text-center">
                <Img src={p.img} alt={p.name} className="rounded-2xl w-full h-auto object-cover aspect-[3/4]" sizes={SIZES.portrait} />
                <h3 className="mt-4 font-semibold text-lg">{p.name}</h3>
                <p className="text-sm opacity-75">{t.teamRoles[i]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
